import { useContent } from "../hooks/useContent";
import { useInView } from "../hooks/useInView";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Truck, Clock, Star } from "lucide-react";

const categoryOrder = ["laundry", "mens", "womens", "woolen", "household", "shoesBags"];

export const PricingSection = () => {
  const [ref, isInView] = useInView();
  const { getSection } = useContent();
  const pricingData = getSection("pricing") || {};
  const availableCategories = categoryOrder.filter((k) => pricingData[k]);

  const renderTable = (category) => {
    const data = pricingData[category];
    if (!data) return null;

    if (category === "laundry" || category === "household" || category === "shoesBags") {
      return (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-sky-50/50">
                <TableHead className="text-slate-700 font-semibold">{data.columns[0]}</TableHead>
                <TableHead className="text-right text-slate-700 font-semibold">{data.columns[1]}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item, i) => (
                <TableRow key={i} className="hover:bg-sky-50/30 transition-colors">
                  <TableCell className="font-medium text-slate-800">{item.name}</TableCell>
                  <TableCell className="text-right font-semibold text-sky-700">{item.price}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.note && (
            <p className="text-xs text-slate-400 mt-3 px-1">* {data.note}</p>
          )}
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-sky-50/50">
              <TableHead className="text-slate-700 font-semibold">{data.columns[0]}</TableHead>
              <TableHead className="text-right text-slate-700 font-semibold">{data.columns[1]}</TableHead>
              <TableHead className="text-right text-slate-700 font-semibold">{data.columns[2]}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((item, i) => (
              <TableRow key={i} className="hover:bg-sky-50/30 transition-colors">
                <TableCell className="font-medium text-slate-800">{item.name}</TableCell>
                <TableCell className="text-right font-semibold text-sky-700">{item.dryClean}</TableCell>
                <TableCell className="text-right font-semibold text-lime-600">{item.steamIron}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <section id="pricing" className="py-16 lg:py-20 bg-slate-50/70 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 w-[600px] h-[600px] bg-sky-50/40 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12" ref={ref}>
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
            Our Price List
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-5">
            Transparent Pricing,
            <br />
            No Surprises
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Prices starting from Rs. 10. Free pickup & delivery included with every order.
          </p>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-sky-100 shadow-sm">
            <Truck className="w-4 h-4 text-sky-600" />
            <span className="text-sm font-medium text-slate-600">Free Pickup & Delivery</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-sky-100 shadow-sm">
            <Clock className="w-4 h-4 text-lime-500" />
            <span className="text-sm font-medium text-slate-600">Delivery Within 24 Hrs</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-sky-100 shadow-sm">
            <Star className="w-4 h-4 text-lime-500" />
            <span className="text-sm font-medium text-slate-600">Professional Fabric Care</span>
          </div>
        </div>

        {/* Tabbed Pricing */}
        <div
          className={`bg-white rounded-3xl border border-slate-100 shadow-lg shadow-sky-900/5 overflow-hidden transition-all duration-700 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <Tabs defaultValue={availableCategories[0] || "laundry"} className="w-full">
            <div className="border-b border-slate-100 bg-white">
              <TabsList className="w-full h-auto p-0 bg-transparent rounded-none flex overflow-x-auto gap-0">
                {availableCategories.map((key) => (
                  <TabsTrigger
                    key={key}
                    value={key}
                    className="flex-shrink-0 px-5 py-4 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-sky-600 data-[state=active]:text-sky-600 data-[state=active]:bg-sky-50/50 text-slate-500 hover:text-slate-700 transition-all"
                  >
                    {pricingData[key].label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {availableCategories.map((key) => (
              <TabsContent key={key} value={key} className="p-6 mt-0">
                {renderTable(key)}
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <p className="text-center text-sm text-slate-400 mt-8">
          All prices are starting from. Actual prices may vary based on garment type and condition.
        </p>
      </div>
    </section>
  );
};
